import AWS from 'aws-sdk';
import { Auth } from 'aws-amplify';
import {
  Button,
  FormField,
  Modal,
  SpaceBetween,
  Spinner,
  Container,
  Select,
  Input,
  Tabs,
  Cards,
  Link,
  Flashbar,
  Form,
  DatePicker,
  SelectProps,
  InputProps,
  Multiselect,
} from '@amzn/awsui-components-react';
import {
  Camera,
  CameraLink,
  Step,
  VMS,
} from 'src/API';
import {
  debug,
  getParcelTracking,
  validDate,
} from 'src/utils/utils';
import {
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import Header from '@amzn/awsui-components-react/polaris/header';
import { QueryKeys, StepOptions } from 'src/constants';
import ReactPlayer from 'react-player';
import { parcelPlaybackBaseState } from 'src/stores/app';
import { useBundle } from '@amzn/react-arb-tools';
import { useQuery } from '@tanstack/react-query';
import { addSeconds } from 'date-fns/addSeconds';
import { addDays } from 'date-fns';

class NoParcelDataFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoParcelDataFoundError';
  }
}

export interface CardItemsInterface {
  stepName: string;
  items: {
    name: string;
    url: string;
    cameraId: string;
    startTime: string;
    endTime: string;
  }[];
}

export interface ModalComponentInterface {
  url: string;
  isModalLoading: boolean;
}

export interface Device {
  cameraName: string;
  cameraSystemId: string;
}
export interface SelectOptionInterface {
  label: string;
  value: string;
}

interface CardComponentProps {
  stepName: string;
  cardItems: CardItemsInterface[];
  loading: boolean;
  openModal: (cameraId: string, prevButton: boolean, nextButton: boolean, startTime: string, endTime: string) => void;
}

export interface CancelCreateSystemInterface { (): void; }

export default function ParcelSearchTablePanel() {

  const cameraLinksQuery = useQuery({ queryKey: [QueryKeys.cameraLinks], enabled: false });
  const camerasQuery = useQuery({ queryKey: [QueryKeys.cameras], enabled: false });

  const [bundle, isBundleLoading] = useBundle('components.ParcelSearch.TablePanel');
  const [cameraLoadProgress, setCameraLoadProgress] = useState<Record<string, {loaded: number, total: number}>>({});
  const [cardItems, setCardItems] = useState<CardItemsInterface[]>([]);
  const [cardLoading, setCardLoading] = useState<boolean>(false);
  const [induct, setInduct] = useState<boolean>(true);
  const [inductLabel, setInductLabel] = useState<string>(`${Step.Induct} - No Time Stamp`);
  const [invalidSearchDate, setInvalidSearchDate] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [isPrevButtonDisabled, setIsPrevButtonDisabled] = useState<boolean>(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState<boolean>(false);
  const [loadingTrackingData, setLoadingTrackingData] = useState<boolean>(false);
  const [modalCameraId, setModalCameraId] = useState<string>('');
  const [modalUrl, setModalUrl] = useState<string>('');
  const [modalVideoStartTime, setModalVideoStartTime] = useState<string>('');
  const [modalVideoEndTime, setModalVideoEndTime] = useState<string>('');
  const [modalVideoVisible, setModalVideoVisible] = useState<boolean>(false);
  const [noParcelDataFound, setNoParcelDataFound] = useState<boolean>(false);
  const [pick, setPick] = useState<boolean>(true);
  const [pickLabel, setPickLabel] = useState<string>(`${Step.Pick} - No Timestamp`);
  const [postRoll, setPostRoll] = useState<string>('15 seconds');
  const [postUrl, setPostUrl] = useState<string>('');
  const [preRoll, setPreRoll] = useState<string>('15 seconds');
  const [preUrl, setPreUrl] = useState<string>('');
  const [searchStartDate, setSearchStartDate] = useState<string>(addDays(new Date(), -7).toISOString().split('T')[0]);
  const [selectedSteps, setSelectedSteps] = useState(
    StepOptions.map(s => ({
      label: s.label as string,
      value: s.value,
    })),
  );
  const [stage, setStage] = useState<boolean>(true);
  const [stagingLabel, setStagingLabel] = useState<string>('Staging - No Timestamp');
  const [stepLoadingStatus, setStepLoadingStatus] = useState<Record<string, boolean>>({});
  const [stow, setStow] = useState<boolean>(true);
  const [stowLabel, setStowLabel] = useState<string>(`${Step.Stow} - No Timestamp`);
  const [trackingId, setTrackingID] = useState<string>('');

  let rollOptions: SelectOptionInterface[] = [
    {label: '15 seconds', value: '1'},
    {label: '30 seconds', value: '2'},
    {label: '45 seconds', value: '3'},
    {label: '1 minute', value: '4'}
  ];

  // Function to call AWS Lambda
  const callLambda = async (siteCode: string, cameraId: string, startTime: string, endTime: string): Promise<string> => {
    debug(`ParcelSearchTablePanel() callLambda() siteCode is ${siteCode} cameraId is ${cameraId} startTime is ${startTime} endTime is ${endTime}`);

    const params = {
      FunctionName: parcelPlaybackBaseState.get().selectedSite?.vms === VMS.Milestone
        ? 'MileStoneVideoRetrievalLambda'
        : 'BoschVideoRetrievalLambda',
      Payload: JSON.stringify({
        siteCode,
        cameraId,
        startTime,
        endTime,
      })
    };
    debug(`ParcelSearchTablePanel() callLambda() params is ${JSON.stringify(params)}`);

    try {
      AWS.config.update({
        httpOptions: {
          timeout: 420000
        }
      });
      const getVideoLambda = new AWS.Lambda({ region: 'us-east-1' });
      const response = await getVideoLambda.invoke(params).promise();
      debug(`ParcelSearchTablePanel() callLambda() response is ${JSON.stringify(response)}`);
      const result = JSON.parse(JSON.parse(response.Payload as string).body).signedUrl;
      // Assuming the URL is in a field called 'url' in the Lambda response
      return result;
    } catch (error) {
      console.error('Error invoking Lambda function:', error);
      throw error;
    }

  };

  const fetchParcelData = async (trackingId: string) => {
    debug(`ParcelSearchTablePanel() fetchParcelData() trackingId is ${trackingId}`);

    resetFetchParcelData();

    setPick(selectedSteps.find(s => s.value === Step.Pick) === undefined);
    setInduct(selectedSteps.find(s => s.value === Step.Induct) === undefined);
    setStage(selectedSteps.find(s => s.value === Step.Staging) === undefined);
    setStow(selectedSteps.find(s => s.value === Step.Stow) === undefined);
    setLoadingTrackingData(true);

    const cameraLinks = (cameraLinksQuery.data as CameraLink[]) ?? [];
    debug(`ParcelSearchTablePanel() fetchParcelData() cameraLinks.length is ${cameraLinks.length}`);
    setCardLoading(true);

    const initializeLoadingStatus = () => {
      const initialStatus: Record<string, boolean> = {};
      cameraLinks.forEach(cl => {
        if (selectedSteps.find(s => s.label === cl.stepName)) initialStatus[cl.stepName] = true;
      });
      setStepLoadingStatus(initialStatus);
    };

    const initializeCameraLoadProgress = () => {
      const progress: Record<string, {loaded: number, total: number}> = {};
      cameraLinks.forEach(cl => {
        if (selectedSteps.find(s => s.label === cl.stepName)) progress[cl.stepName] = {loaded:0 , total: cl.cameras?.length ?? 0};
      });
      setCameraLoadProgress(progress);
    }

    AWS.config.credentials = await Auth.currentCredentials();
    AWS.config.region = 'us-east-1';

    try {
      const newSearchDate = (new Date(searchStartDate).setDate((new Date(searchStartDate)).getDate()));
      const parcelTrackingData = await getParcelTracking(
        parcelPlaybackBaseState.get().selectedSite?.siteCode!,
        trackingId,
        (new Date(newSearchDate)).toISOString().split('T')[0]);
      if (!parcelTrackingData) throw new NoParcelDataFoundError('No parcel tracking data found');
      if (parcelTrackingData?.pickTime){
        setPickLabel(`Pick @ ${parcelTrackingData.pickTime}`);
        setPick(false);
        setPick(selectedSteps.find(s => s.value === Step.Pick) === undefined);
      }
      if (parcelTrackingData?.stowTime){
        setStowLabel(`Stow @ ${parcelTrackingData.stowTime}`);
        setStow(false);
        setStow(selectedSteps.find(s => s.value === Step.Stow) === undefined);
      }
      if (parcelTrackingData?.inductTime){
        setInductLabel(`Induct @ ${parcelTrackingData.inductTime}`)
        setInduct(false);
        setInduct(selectedSteps.find(s => s.value === Step.Induct) === undefined);
      }
      if (parcelTrackingData?.stageTime){
        setStagingLabel(`Stage @ ${parcelTrackingData.stageTime}`)
        setStage(false);
        setStage(selectedSteps.find(s => s.value === Step.Staging) === undefined);
      }
      setLoadingTrackingData(false);
      
      const getCameraUrls = async (): Promise<void> => {
        initializeLoadingStatus();
        initializeCameraLoadProgress();

        try {
          for (const cameraLink of cameraLinks.filter(cl => selectedSteps.find(s => s.label === cl.stepName))) {
            cameraLink?.cameras?.forEach(async camera => {
              if (!camera || !camera.systemIdentifier) return;
              let time = '';
              switch (cameraLink.stepName) {
                case Step.Induct:
                  time = parcelTrackingData?.inductTime || '';
                  break;
                case Step.Stow:
                  time = parcelTrackingData?.stowTime || '';
                  break;
                case Step.Pick:
                  time = parcelTrackingData?.pickTime || '';
                  break;
                case Step.Staging:
                  time = parcelTrackingData?.stageTime || '';
                  break;
              }

              let preRollSeconds;
              switch (preRoll) {
                case '15 seconds':
                  preRollSeconds = 15;
                  break;
                case '30 seconds':
                  preRollSeconds = 30;
                  break;
                case '45 seconds':
                  preRollSeconds = 45;
                  break;
                case '1 minute':
                  preRollSeconds = 60;
                  break;
                default:
                  preRollSeconds = 15;
              }

              let postRollSeconds;
              switch (preRoll) {
                case '15 seconds':
                  postRollSeconds = 15;
                  break;
                case '30 seconds':
                  postRollSeconds = 30;
                  break;
                case '45 seconds':
                  postRollSeconds = 45;
                  break;
                case '1 minute':
                  postRollSeconds = 60;
                  break;
                default:
                  postRollSeconds = 15;
              }

              const startDate = new Date(time.replace(' ', 'T'));
              startDate.setSeconds(startDate.getSeconds() - preRollSeconds);
              const endDate = new Date(time.replace(' ', 'T'));
              endDate.setSeconds(endDate.getSeconds() + postRollSeconds);
              const startTime = startDate.toISOString();
              const endTime = endDate.toISOString();

              const cameras = camerasQuery.data as Camera[] ?? [];
              const cameraName = cameras.find(c => c.systemIdentifier === camera.systemIdentifier)?.name ?? '';
              const cameraId = camera.systemIdentifier;
              // Fetch the video URL for the current device
              let url: string = '';
              try {
                url = await callLambda(parcelPlaybackBaseState.get().selectedSite!.siteCode, cameraId, startTime, endTime);
                if (!url) url = '';
              } catch (error) {
                console.error(error);
              }
              debug(`ParcelSearchTablePanel() fetchParcelData() getCameraUrls() url is ${url}`);
              setCameraLoadProgress(prevProgress => ({
                ...prevProgress,
                [cameraLink.stepName] : {
                  ...prevProgress[cameraLink.stepName], 
                  loaded: url === '' ? prevProgress[cameraLink.stepName].loaded : prevProgress[cameraLink.stepName].loaded + 1
                }
              }));
              // Create the new card item
              const newCardItem: CardItemsInterface = {
                stepName: cameraLink.stepName,
                items: [
                  {
                    cameraId: cameraId,
                    endTime: endTime,
                    name: cameraName,
                    startTime: startTime,
                    url: url,
                  }
                ]
              };
              // Update the card items incrementally
              setCardItems((prevItems: CardItemsInterface[] = []) => {
                const existingStep = prevItems.find((item: CardItemsInterface) => item.stepName === cameraLink.stepName);
                if (existingStep) {
                  // Append the new item to the existing stage
                  const updatedItems = [
                    ...existingStep.items,
                    {
                      endTime,
                      name: cameraName,
                      url,
                      cameraId,
                      startTime,
                    }
                  ];
                  // As soon as at least one camera has a URL, stop showing the loading interface
                  setStepLoadingStatus(prevStatus => ({
                    ...prevStatus,
                    [cameraLink.stepName]: false
                  }));
                  return prevItems.map(item =>
                    item.stepName === cameraLink.stepName
                      ? {
                        ...item,
                        items: updatedItems
                      }
                      : item
                  );
                } else {
                  // Add a new stage with the new item
                  setStepLoadingStatus(prevStatus => ({
                    ...prevStatus,
                    [cameraLink.stepName]: false
                  }));
                  return [
                    ...prevItems,
                    newCardItem,
                  ];
                }
              });
            });
          }
        } catch (error) {
          console.error('Error fetching camera URLs: ', error);
          setCardLoading(false);
          setLoadingTrackingData(false);
          throw error;
        } finally {
          setCardLoading(false);
        }
      };

      try {
        await getCameraUrls();
      } catch (error) {
        console.error(error);
        setLoadingTrackingData(false);
        throw error;
      }
    } catch(error) {
      setLoadingTrackingData(false);
      setCardLoading(false);
      if (error instanceof NoParcelDataFoundError) {
        setNoParcelDataFound(true);
      } else {
        console.error('Error:', error);
        throw error;
      }
    }
  };

  const resetFetchParcelData = () => {
    setInductLabel(`${Step.Induct} - No Timestamp`);
    setPickLabel(`${Step.Pick} - No Timestamp`);
    setStagingLabel('Stage - No Timestamp');
    setStowLabel(`${Step.Stow} - No Timestamp`);
    setCardItems([]);
    setCardLoading(false);
    setLoadingTrackingData(false);
    setCameraLoadProgress({});
    setNoParcelDataFound(false);
  }

  const getTabLabelWithProgress = (label: string, stepName: string) => {
    const progress = cameraLoadProgress[stepName];
    if (progress) {
      return(
        <div>
          <span>{label}</span>
          <br/>
          <span>{progress.loaded}/{progress.total} cameras loaded</span>
        </div>
      );
    } else {
      return `${label}`;
    }
  }

  const openModal = async (cameraId: string, prevButton: boolean, nextButton: boolean, startTime: string, endTime: string) => {
    setIsModalLoading(true);
    setModalVideoStartTime(startTime);
    setModalVideoEndTime(endTime);
    setModalVideoVisible(true);
    setModalCameraId(cameraId);
    let url = '';
    if (prevButton && !preUrl) {
      const prevStartTime = addSeconds(new Date(startTime), -30);
      const prevEndTime = addSeconds(new Date(endTime), -30);
      url = await callLambda(parcelPlaybackBaseState.get().selectedSite?.siteCode!, cameraId, prevStartTime.toISOString(), prevEndTime.toISOString());
      setPreUrl(url);
    } else if (prevButton){
      url = preUrl
    }
    
    if (nextButton && !postUrl) {
      const nextStartTime = addSeconds(new Date(startTime), +30);
      const nextEndTime = addSeconds(new Date(endTime), +30);
      url = await callLambda(parcelPlaybackBaseState.get().selectedSite?.siteCode!, cameraId, nextStartTime.toISOString(), nextEndTime.toISOString());
      setPostUrl(url);
    } else if (nextButton) {
      url = postUrl
    }
    
    setModalUrl(url);
    setIsModalLoading(false);
  };

  const updateModal = async (cameraId: string, prevButton: boolean, nextButton: boolean, startTime: string, endTime: string) => {
    setIsModalLoading(true);
    let update_url = '';
    if (prevButton) {
      setIsPrevButtonDisabled(true);
      setIsNextButtonDisabled(false);
      if (!preUrl) {
        const prevStartTime = addSeconds(new Date(startTime), -30);
        const prevEndTime = addSeconds(new Date(endTime), -30);
        update_url = await callLambda(parcelPlaybackBaseState.get().selectedSite?.siteCode!, cameraId, prevStartTime.toISOString(), prevEndTime.toISOString());
        setPreUrl(update_url);
      } else {
        update_url = preUrl;
      }
    }
    if (nextButton) {
      setIsNextButtonDisabled(true);
      setIsPrevButtonDisabled(false);
      if (!postUrl) {
        const nextStartTime = addSeconds(new Date(startTime), +30);
        const nextEndTime = addSeconds(new Date(endTime), +30);
        update_url = await callLambda(parcelPlaybackBaseState.get().selectedSite?.siteCode!, cameraId, nextStartTime.toISOString(), nextEndTime.toISOString());
        setPostUrl(update_url);
      } else {
        update_url = postUrl;
      }
    }
    setModalUrl(update_url);
    setIsModalLoading(false);
  };

  const closeModal = () => {
    setModalVideoVisible(false);
  };

  const CardComponent: React.FC<CardComponentProps> = memo(({
    stepName,
    cardItems,
    loading,
    openModal,
  }) => {
    const getItemsForStage = (stepName: string, cardItems: CardItemsInterface[]): { name: string; url: string; cameraId: string, startTime: string, endTime: string }[] | undefined => {
      const stage = cardItems.find(item => item.stepName === stepName);
      return stage ? stage.items : undefined;
    };
    const itemsForStage = getItemsForStage(stepName, cardItems);

    return(
      <>
         {loading && (!itemsForStage || itemsForStage.length === 0)
         ?
          <div>
            <Flashbar
              items={[
                {
                  type: 'success',
                  loading: true,
                  content: 'Loading video, this will take several minutes...',
                  id: 'ParcelSearchLoadingVideo',
                },
              ]}
            />
          </div>
        :
          <Cards
            cardDefinition={{
              header: (item) => item.name,
              sections: [
                {
                  id: 'Camera name TBD',
                  content: (item) => {
                    if (item.url === '') {
                      return <div>Error loading video.  For more information please see the <a href='https://w.amazon.com/bin/view/AccSys/ParcelPlayback/' target='_blank'>User Guide</a>.</div>;
                    }
                    return <ReactPlayer url={item.url} controls width='100%' />;
                  }
                },
                {
                  id: 'pre-roll/post-roll',
                  content: (item) => (
                    <>
                      <SpaceBetween direction='horizontal' size='s'>
                        {item.url !== ''
                        &&
                        <>
                          <Link onClick={() => openModal(item.cameraId, true, false, item.startTime, item.endTime)}>
                            Pull previous 30 seconds
                          </Link>
                          <Link onClick={() => openModal(item.cameraId, false, true, item.startTime, item.endTime)}>
                            Pull next 30 seconds
                          </Link>
                        </>}
                      </SpaceBetween>
                    </>
                  ),
                },
              ],
            }}
            cardsPerRow={[
              { cards: 1 },
              { minWidth: 300, cards: 3 },
            ]}
            items={itemsForStage ?? []}
            loadingText='Loading camera feed...'
            loading={loading && !itemsForStage} // Show the loading state only if no items have been retrieved yet
          />
        }
      </>);
  });

  const preRollFieldOnChangeHandler = (detail: SelectProps.ChangeDetail) => {
    setPreRoll(detail.selectedOption.label ?? '');
  };

  const postRollFieldOnChangeHandler = (detail: SelectProps.ChangeDetail) => {
    setPostRoll(detail.selectedOption.label ?? '');
  };

  const trackingIdInputRef = useRef<InputProps.Ref>(null);

  useEffect(() => {
    if (trackingIdInputRef.current) {
      trackingIdInputRef.current.focus();
    }
  }, [parcelPlaybackBaseState.get().selectedSite?.siteCode]);

  if (isBundleLoading) return <Spinner/>;
  
  return (
    <Container>
      <form onSubmit={async e => {
        e.preventDefault();
        await fetchParcelData(trackingId);
      }}>
        <Form>
          <Header>Parcel Search</Header>
          <SpaceBetween alignItems='end' direction='horizontal' size='xs'>
            <FormField label='Tracking Id'>
              <Input
                ariaRequired={true}
                autoFocus
                disabled={
                  loadingTrackingData
                  || stepLoadingStatus[Step.Induct]
                  || stepLoadingStatus[Step.Stow]
                  || stepLoadingStatus[Step.Pick]
                  || stepLoadingStatus[Step.Staging]
                }
                onChange={({ detail }) => setTrackingID(detail.value)}
                placeholder='Enter Tracking Id'
                ref={trackingIdInputRef}
                value={trackingId}
              />
            </FormField>
            <FormField label='Start Date'>
              <DatePicker
                ariaRequired={true}
                disabled={
                  loadingTrackingData
                  || stepLoadingStatus[Step.Induct]
                  || stepLoadingStatus[Step.Stow]
                  || stepLoadingStatus[Step.Pick]
                  || stepLoadingStatus[Step.Staging]
                }
                invalid={invalidSearchDate}
                isDateEnabled={date => {
                  const [month, day, year] = [
                    (date.getMonth() + 1).toString().padStart(2, '0'),
                    date.getDate().toString().padStart(2, '0'),
                    date.getFullYear(),
                  ];
                  const convertedDate = new Date(`${year}-${month}-${day} 00:00:00`);
                  return validDate(convertedDate, 30);
                }}
                onChange={({ detail }) => {
                  let newSearchDate: string = detail.value;
                  if (newSearchDate === '') newSearchDate = new Date().toISOString().split('T')[0];
                  if (!validDate(new Date(newSearchDate!), 30)) {
                    setInvalidSearchDate(true);
                  } else {
                    setInvalidSearchDate(false);
                  }
                  setSearchStartDate(newSearchDate!);
                }}
                placeholder={new Date().toISOString().split('T')[0]}
                value={searchStartDate}
              />
            </FormField>
            <FormField label='PreRoll Options'>
              <Select
                disabled={
                  loadingTrackingData
                  || stepLoadingStatus[Step.Induct]
                  || stepLoadingStatus[Step.Stow]
                  || stepLoadingStatus[Step.Pick]
                  || stepLoadingStatus[Step.Staging]
                }
                filteringType='auto'
                loadingText='Loading...'
                onChange={({ detail }) => preRollFieldOnChangeHandler(detail)}
                options={rollOptions}
                selectedAriaLabel='Selected'
                selectedOption={preRoll ? { label: preRoll, value: rollOptions.find(r => r.label === preRoll)?.value } : null}
              />
            </FormField>
            <FormField label='PostRoll Options'>
              <Select
                disabled={
                  loadingTrackingData
                  || stepLoadingStatus[Step.Induct]
                  || stepLoadingStatus[Step.Stow]
                  || stepLoadingStatus[Step.Pick]
                  || stepLoadingStatus[Step.Staging]
                }
                filteringType='auto'
                loadingText='Loading...'
                onChange={({ detail }) => postRollFieldOnChangeHandler(detail)}
                options={rollOptions}
                selectedAriaLabel='Selected'
                selectedOption={postRoll ? { label: postRoll, value: rollOptions.find(r => r.label === postRoll)?.value } : null}
              />
            </FormField>
            <FormField label='Steps'>
              <Multiselect
                disabled={
                  loadingTrackingData
                  || stepLoadingStatus[Step.Induct]
                  || stepLoadingStatus[Step.Stow]
                  || stepLoadingStatus[Step.Pick]
                  || stepLoadingStatus[Step.Staging]
                }
                hideTokens
                options={StepOptions.map(s => (
                  {
                    label: s.label as string,
                    value: s.value,
                  }
                ))}
                onChange={({ detail }) => {
                  setSelectedSteps((detail.selectedOptions as unknown as { label: string, value: string }[]));
                }}
                placeholder={
                  selectedSteps.length === 0
                  ? 'Select steps'
                  : selectedSteps.map((value) => ` ${value.label}`).toString()
                }
                selectedOptions={selectedSteps}
              />
            </FormField>
            <Button
              disabled={
                trackingId === ''
                || searchStartDate === ''
                || loadingTrackingData
                || invalidSearchDate
                || selectedSteps.length === 0}
              loading={
                loadingTrackingData
                || stepLoadingStatus[Step.Induct]
                || stepLoadingStatus[Step.Stow]
                || stepLoadingStatus[Step.Pick]
                || stepLoadingStatus[Step.Staging]
              }
              onClick={() => fetchParcelData(trackingId)}
              variant='primary'
            >
              Search
            </Button>
          </SpaceBetween>
          <br />
          {noParcelDataFound
          &&
          <Flashbar
            items={[
              {
                type: 'warning',
                content: 'No parcel data found.',
                dismissible: true,
                onDismiss: () => setNoParcelDataFound(false),
                id: 'NoParcelDataFoundMessage',
              },
            ]}
          />}
          <Tabs
            tabs={[
              {
                label:
                  <>
                    {stepLoadingStatus[Step.Induct] && <Spinner size='normal'/>}{getTabLabelWithProgress(inductLabel, Step.Induct)}
                  </>,
                id: 'first',
                content:
                  <CardComponent
                    stepName={Step.Induct}
                    cardItems={cardItems ?? []}
                    loading={stepLoadingStatus[Step.Induct]}
                    openModal={openModal}
                  />,
                disabled: induct,
              },
              {
                label:
                  <>
                    {stepLoadingStatus[Step.Stow] && <Spinner size='normal'/>}{getTabLabelWithProgress(stowLabel, Step.Stow)}
                  </>,
                id: 'second',
                content:
                  <CardComponent
                    stepName={Step.Stow}
                    cardItems={cardItems ?? []}
                    loading={stepLoadingStatus[Step.Stow]}
                    openModal={openModal}
                  />,
                disabled: stow,
              },
              {
                label:
                  <>
                    {stepLoadingStatus[Step.Pick] && <Spinner size='normal'/>}{getTabLabelWithProgress(pickLabel, Step.Pick)}
                  </>,
                id: 'third',
                content:
                  <CardComponent
                    stepName={Step.Pick}
                    cardItems={cardItems ?? []}
                    loading={stepLoadingStatus[Step.Pick]}
                    openModal={openModal}
                  />,
                disabled: pick,
              },
              {
                label:
                  <>
                    {stepLoadingStatus[Step.Staging] && <Spinner size='normal'/>}{getTabLabelWithProgress(stagingLabel, Step.Staging)}
                  </>,
                id: 'fourth',
                content:
                  <CardComponent
                    stepName={Step.Staging}
                    cardItems={cardItems ?? []}
                    loading={stepLoadingStatus[Step.Staging]}
                    openModal={openModal}
                  />,
                disabled: stage,
              },
            ]}
          />
          {modalVideoVisible // for proper unmounting
          &&
          <Modal
            visible={modalVideoVisible}
            onDismiss={closeModal}
            footer={
              <SpaceBetween direction='horizontal' size='s'>
                <Button
                  disabled={isPrevButtonDisabled || isModalLoading}
                  onClick={() => updateModal(modalCameraId, true, false, modalVideoStartTime, modalVideoEndTime)}
                  variant={isNextButtonDisabled ? 'primary' : 'normal'}
                >
                  Previous 30 seconds
                </Button>
                <Button
                  disabled={isNextButtonDisabled || isModalLoading}
                  onClick={() => updateModal(modalCameraId, false, true, modalVideoStartTime, modalVideoEndTime)}
                  variant={isPrevButtonDisabled ? 'primary' : 'normal'}
                >
                  Next 30 seconds
                </Button>
              </SpaceBetween>
            }
          >
            {isModalLoading ? (
              <div>
                <Flashbar
                  items={[
                    {
                      type: 'success',
                      loading: true,
                      content: 'Loading video, this will take several minutes...',
                      id: 'ParcelPlayBackModelLoadingVideo',
                    },
                  ]}
                />
              </div>
            ) : (
              <>
                {modalUrl === ''
                &&
                <div>
                  Error loading video.  For more information please see the <a href='https://w.amazon.com/bin/view/AccSys/ParcelPlayback/' target='_blank'>User Guide</a>.
                </div>}
                {modalUrl !== ''
                &&
                <ReactPlayer url={modalUrl} controls width='100%' />}
              </>
            )}
          </Modal>}
        </Form>
      </form>
    </Container>);
}